/**
 * useOpenPaletteShortcut — global ⌘K / Ctrl+K opens Search.
 *
 * Adds ONE `window` `keydown` listener that, on Cmd+K (mac) / Ctrl+K
 * (Windows/Linux), calls `preventDefault()` and dispatches the
 * `proof:open-palette` CustomEvent. The already-mounted <Palette> listens for
 * that event and opens itself — so this hook is a thin, standalone accelerator
 * onto the existing open path. The palette manages its own open state, so
 * pressing the shortcut while it is already open is a harmless no-op.
 *
 * Mount this ONCE, high in the authenticated /app tree (WorkspaceContainer in
 * `apps/web/src/routes/app.tsx`), so the shortcut works on BOTH the Library
 * landing and the Workspace recipe view. A single window listener covers every
 * mount; do not mount it per-surface.
 *
 * Wishlist exception: `.plan/wishlist/keyboard-shortcuts-and-palette.md`
 * (modifier-prefixed ⌘K opens Search), permitted by design amendment
 * `.plan/features/2026-04-29-design-amendments.md §3.3` — zero app-defined
 * single-key shortcuts; modifier-prefixed bindings are allowed. This ships
 * only the single ⌘K → open-Search binding; the broader keyboard catalogue +
 * command-palette layer stays wishlisted.
 *
 * Collision check (2026-06-14): the recipe editor
 * (`apps/web/src/features/editor/TipTapEditor.tsx`) binds only `Mod-z`,
 * `Mod-Shift-z`, `Ctrl-y`, and `ArrowRight` — NO `Mod-k`. The old
 * `apps/web/src/lib/keyboard.ts` guard module (PR #64) no longer exists.
 * `⌘K` / `Ctrl+K` therefore collides with no in-app binding, and has no
 * critical native browser meaning in plain inputs/textareas — so no
 * editor-focus or input-focus guard is required.
 */

import { useEffect } from "react";

/**
 * Installs the global ⌘K / Ctrl+K → open-Search keyboard shortcut for the
 * lifetime of the calling component. The listener is removed on unmount.
 */
export function useOpenPaletteShortcut(): void {
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent): void {
      // Require Cmd (mac) or Ctrl (Win/Linux). Bare "k" must do nothing — the
      // editor is a typing-first surface and single-key shortcuts are banned
      // (keyboard policy; .plan/wishlist/keyboard-shortcuts-and-palette.md).
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key.toLowerCase() !== "k") return;
      // A modifier-PLUS-shift combo (⌘⇧K) is reserved for the future
      // command-mode palette (still wishlisted) — leave it for that binding.
      if (e.shiftKey) return;

      e.preventDefault();
      window.dispatchEvent(new CustomEvent("proof:open-palette"));
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);
}
